// CannotRefreshBand — a screen that cannot refresh SAYS SO (ADR-074 §9): one calm
// strip in the frame's pinned top, on any page inside a project, while the tab's
// live client reports that it has lost the server past the server's own
// threshold of silence. It speaks of the CONNECTION, so it is true on every page
// — a list, a form, a wizard. Silent the rest of the time: there is no "updated
// … ago" anywhere, because a healthy screen would say "3 s ago" for ever.
//
//   • the time is the SERVER's clock at the last good contact — a wrong local
//     clock cannot lie about it (the date rides along when that was not today);
//   • two tones — calm, then stronger after a long failure (the client decides
//     which, from the server's numbers), the SAME words in both;
//   • it leaves by itself at the first good contact, when everything live on the
//     page re-reads at once; Retry checks now, and spins while the check the
//     PERSON asked for is out — the tab's own checks move nothing here.
//
// ANNOUNCED ONCE, by a live region of its own that is ALWAYS mounted: a region
// born already filled is not announced, so the words are poured into a polite
// `status` that was there first. The visible strip carries no role (it is
// persistent chrome — the seat's rule, ProjectStrips — and must not be a second
// announcement). It never takes focus; when it leaves while its Retry HOLDS the
// focus, the focus goes to the page's main region instead of falling off the page.
//
// Everything it shows is the live store's; it owns no timer and reads no token.

import { useLayoutEffect, useRef } from 'react';
import { useTranslation } from 'react-i18next';
import { useLiveStore } from '../../stores/liveStore.js';
import { liveClient } from '../../lib/live/live.js';
import { formatDate } from '../../lib/format.js';
import { Banner } from '../ui/Banner.js';
import { Button } from '../ui/Button.js';
import { Spinner } from '../ui/Spinner.js';
import { pageBox, pageGutter } from './pageBox.js';
import { useDisplayZone } from '../../hooks/useDisplayZone.js';

export function CannotRefreshBand() {
  const { t } = useTranslation('nav');
  const status = useLiveStore((s) => s.status);
  const lastContactAt = useLiveStore((s) => s.lastContactAt);
  const sinceToday = useLiveStore((s) => s.sinceToday);
  // The tab's last contact is the person's own fact (ADR-082 §2): said in the viewer's zone.
  const display = useDisplayZone({ kind: 'person' });
  const zone = display.zone ?? 'UTC';

  const cutOff = status === 'cut-off' || status === 'cut-off-long';
  const time = lastContactAt === null ? null : formatDate(lastContactAt, sinceToday ? 'time' : 'datetime', { timeZone: zone });
  const since = time === null ? t('banner.cutOff.unknown') : t(sinceToday ? 'banner.cutOff.since' : 'banner.cutOff.sinceDay', { time });
  const consequence = t('banner.cutOff.consequence');

  return (
    <>
      <div role="status" className="sr-only">
        {cutOff && (
          <>
            <span>{since}</span> <span>{consequence}</span>
          </>
        )}
      </div>
      {cutOff && <Strip long={status === 'cut-off-long'} since={since} consequence={consequence} />}
    </>
  );
}

/** The visible strip — mounted only while the screen is cut off. */
function Strip({ long, since, consequence }: { long: boolean; since: string; consequence: string }) {
  const { t: tc } = useTranslation('common');
  const checking = useLiveStore((s) => s.checking);
  const retry = useRef<HTMLButtonElement>(null);

  // Leaving while Retry holds the focus (the person pressed it, the contact is
  // good): hand the focus to the page's main region. A LAYOUT effect's cleanup —
  // it runs before the button leaves the document, while it still is the focus.
  useLayoutEffect(() => {
    const button = retry.current;
    return () => {
      if (button && document.activeElement === button) document.getElementById('main-content')?.focus({ preventScroll: true });
    };
  }, []);

  return (
    <div className={pageBox}>
      <Banner variant={long ? 'danger' : 'warning'} role="none" align="center" className={pageGutter}>
        <span className="flex flex-wrap items-center justify-center gap-x-2 gap-y-1">
          <span className="font-semibold">{since}</span>
          <span aria-hidden>·</span>
          <span>{consequence}</span>
          <span aria-hidden>·</span>
          {/* Never `loading` (it disables, and a disabled button drops the focus): it
              stays focusable, says it is busy, and a second press does nothing. The
              neutral ink + underline holds its contrast on both tints, both themes;
              the negative margin grows the hit target without growing the strip. */}
          <Button
            ref={retry}
            variant="link"
            tone="neutral"
            className="-my-1 py-1 underline"
            aria-busy={checking || undefined}
            onClick={() => {
              if (!checking) liveClient.checkNow();
            }}
          >
            {checking && <Spinner size={14} />}
            {tc('actions.retry')}
          </Button>
        </span>
      </Banner>
    </div>
  );
}
